import { ButtonHTMLAttributes, MouseEvent } from 'react';
import styled, { css } from 'styled-components';

import { noop } from 'lodash';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import { lima, razzmatazz } from '@paradigm/design-system/src/colors';
import { formatProductQty } from '@paradigm/features/src/unified-rfqs/ui/decimal-format';
import GridCellBase, {
  GridCellProps,
} from '#/components/UnifiedRfqs/components/GridCellBase';

import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';

const PriceContainer = styled.span`
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 86px;
  height: 24px;
  border-radius: 3px;
  background: black;
  font-weight: 600;
  font-size: 14px;
  color: #b3b6ba;
  white-space: nowrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 16px;
    width: 90px;
    height: 28px;
  }
`;

const mutedTextCss = css`
  opacity: 0.3;
  color: white;
`;

export const PriceButton = styled(PriceContainer.withComponent('button'))<{
  isTextMuted?: boolean;
}>`
  transition: background-color 0.16s ease, box-shadow 0.16s ease;

  :not(:disabled):hover {
    background: #121212;
  }
  & :disabled {
    opacity: 0.5;
  }

  ${(props) => props.isTextMuted === true && mutedTextCss}
`;

interface QuotePriceButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly price: string;
}

export function BidPriceButton({ price, ...props }: QuotePriceButtonProps) {
  return (
    <PriceButton
      {...props}
      css={`
        color: ${lima};
      `}
    >
      {price}
    </PriceButton>
  );
}

export function AskPriceButton({ price, ...props }: QuotePriceButtonProps) {
  return (
    <PriceButton
      {...props}
      css={`
        color: ${razzmatazz};
      `}
    >
      {price}
    </PriceButton>
  );
}

interface EmptyPriceProps {
  readonly onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly hidePlaceholder?: boolean;
  readonly disabled?: boolean;
}

export const EmptyPrice = ({
  onClick,
  hidePlaceholder = false,
  disabled = false,
}: EmptyPriceProps) => {
  if (hidePlaceholder) return null;
  return (
    <PriceButton disabled={disabled} onClick={!disabled ? onClick : noop}>
      {NEGATIVE_SIGN}
    </PriceButton>
  );
};

interface QtyButtonProps {
  readonly quantity: string | null;
  readonly productCode?: string | undefined;
  readonly onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  readonly isNormalized?: boolean;
  readonly isDated?: boolean;
  readonly hidePlaceholder?: boolean;
  readonly isTextMuted?: boolean;
  readonly disabled: boolean;
}

export function QtyButton({
  quantity,
  onClick,
  productCode,
  isNormalized = false,
  isDated = false,
  hidePlaceholder = false,
  isTextMuted,
  disabled = false,
}: QtyButtonProps) {
  if (quantity == null && hidePlaceholder) return null;
  const content =
    quantity != null
      ? formatProductQty(quantity, productCode, isNormalized, isDated)
      : NEGATIVE_SIGN;
  return (
    <QtyButtonBase
      disabled={disabled}
      onClick={onClick}
      isTextMuted={isTextMuted}
    >
      {content}
    </QtyButtonBase>
  );
}

const QtyButtonBase = styled.button<{ isTextMuted?: boolean }>`
  padding: 1px 3px;
  border: 0;
  border-radius: 3px;
  background: unset;
  color: inherit;
  font-size: 13px;
  font-weight: inherit;
  transition: color 0.16s ease, box-shadow 0.16s ease;

  :hover {
    color: #ddd;
  }
  :focus {
    box-shadow: 0px 0px 0px 1px rgba(255, 255, 255, 0.7);
  }

  ${(props) => props.isTextMuted === true && mutedTextCss}

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 15px;
  }

  :disabled {
    opacity: 0.5;
  }
`;

export const ActionCell = styled(GridCellBase).attrs<GridCellProps>(() => ({
  mobile: true,
}))<GridCellProps>`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;
